import clsx from 'clsx';

import { Collapse, css, styled } from '@mui/material';
import { IcChevronDownSmallRegular, IcChevronRightSmallRegular } from '@kep/kcds-icons';

import { useCascadeState } from '../../utils';
import { Divider } from '../Divider';
import { DEFAULT_EXPANDED } from './constants';
import { getDynamicClasses, menuSectionItemClasses } from './theme';

import type { MenuSectionItemOwnerState, MenuSectionItemProps } from './types';

/**
 * MenuSectionItem
 *
 * 섹션 컨테이너로 제목과 자식 아이템들을 그룹핑합니다.
 * DropdownMenu2, TransferList, SelectableList 등에서 공유됩니다.
 *
 * @remarks
 * - expandable일 때 클릭으로 확장/축소 가능
 * - controlled (expanded) 또는 uncontrolled (defaultExpanded) 모드 지원
 *
 * @example
 * ```tsx
 * <MenuSectionItem
 *   title="섹션 제목"
 *   expandable
 * >
 *   <MenuOptionItem text="옵션 1" />
 *   <MenuOptionItem text="옵션 2" />
 *   <MenuOptionItem text="옵션 3" />
 * </MenuSectionItem>
 * ```
 */
export function MenuSectionItem(props: MenuSectionItemProps) {
  const {
    ref,
    className,
    title,
    subheader: header,
    children,
    color = 'default',
    expandable = false,
    expanded: expandedProp,
    defaultExpanded = DEFAULT_EXPANDED,
    focusedHeader = false,
    onExpandedChange,
    showDividerAbove = false,
    disabled = false,
    slotProps,
    onHeaderClick,
    ...others
  } = props;

  const [expanded, setExpanded] = useCascadeState({
    value: expandedProp,
    initialValue: defaultExpanded,
    onChange: onExpandedChange,
  });

  const ownerState: MenuSectionItemOwnerState = {
    ...props,
    color,
    expanded: expanded ?? defaultExpanded,
    expandable,
    disabled,
    focusedHeader,
  };

  const handleHeaderClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (disabled) return;

    onHeaderClick?.(event);

    if (expandable) {
      setExpanded(!expanded);
    }
  };

  const ExpandIcon = expandable
    ? expanded
      ? IcChevronDownSmallRegular
      : IcChevronRightSmallRegular
    : null;

  return (
    <>
      {showDividerAbove && <MenuSectionItemDivider ownerState={ownerState} />}
      <MenuSectionItemRoot
        ref={ref}
        className={clsx(className, getDynamicClasses(ownerState))}
        ownerState={ownerState}
        {...others}
      >
        {/* 섹션 헤더 (title이 있을 때만 표시) */}
        {title && (
          <MenuSectionItemHeader
            as={expandable && !disabled ? 'button' : 'div'}
            type={expandable && !disabled ? 'button' : undefined}
            onClick={handleHeaderClick}
            ownerState={ownerState}
            aria-expanded={expandable ? expanded : undefined}
            tabIndex={expandable && !disabled ? 0 : -1}
            {...slotProps?.header}
          >
            {ExpandIcon && (
              <MenuSectionItemExpandIcon ownerState={ownerState}>
                <ExpandIcon />
              </MenuSectionItemExpandIcon>
            )}
            <MenuSectionItemTitle ownerState={ownerState}>{title}</MenuSectionItemTitle>
            {/* 헤더 추가 컨텐츠 */}
          </MenuSectionItemHeader>
        )}
        {header && (
          <MenuSectionItemSubheader ownerState={ownerState}>{header}</MenuSectionItemSubheader>
        )}
        {/* 섹션 컨텐츠 */}
        <Collapse in={expanded} timeout="auto">
          <MenuSectionItemContent ownerState={ownerState}>{children}</MenuSectionItemContent>
        </Collapse>
      </MenuSectionItemRoot>
    </>
  );
}

MenuSectionItem.displayName = 'MenuSectionItem';

// Styled Components
const MenuSectionItemRoot = styled('div', {
  target: menuSectionItemClasses.root,
})<{ ownerState: MenuSectionItemOwnerState }>(
  ({ ownerState }) => css`
    ${ownerState.disabled &&
    css`
      opacity: 0.5;
      pointer-events: none;
    `}
  `,
);

const MenuSectionItemDivider = styled(Divider, {
  target: menuSectionItemClasses.divider,
})<{ ownerState: MenuSectionItemOwnerState }>(
  ({ theme, ownerState }) => css`
    margin: 8px 0;
    border-color: ${ownerState.color !== 'global'
      ? theme.color['border/weaker/default']
      : theme.color['border/global/default']};
  `,
);

const MenuSectionItemHeader = styled('div', {
  target: menuSectionItemClasses.header,
})<{ ownerState: MenuSectionItemOwnerState; type?: 'button' }>(
  ({ theme, ownerState }) => css`
    display: flex;
    align-items: center;
    width: 100%;
    padding: 6px 16px;
    color: ${ownerState.color !== 'global'
      ? theme.color['text/weakest/default']
      : theme.color['text/global/weak/default']};
    user-select: none;
    position: relative;

    /* button 리셋 스타일 */
    border: none;
    background: transparent;
    text-align: left;
    font-family: inherit;

    ${ownerState.focusedHeader && theme.mixins.focusRing('inside', { selectors: { focus: '&' } })}

    ${ownerState.expandable &&
    !ownerState.disabled &&
    css`
      cursor: pointer;

      &:hover {
        background-color: ${ownerState.color !== 'global'
          ? theme.palette.action.hover
          : theme.color['surface/global/transparent/strong/hover']};
      }

      ${theme.mixins.focusRing('inside')};
    `}

    ${!ownerState.expandable &&
    css`
      cursor: default;
      &:hover {
        background-color: ${theme.color['base/transparent']};
      }
    `}
  `,
);

const MenuSectionItemExpandIcon = styled('span', {
  target: menuSectionItemClasses.expandIcon,
})<{ ownerState: MenuSectionItemOwnerState }>(
  () => css`
    display: inline-flex;
    align-items: center;
    margin-right: 4px;
    font-size: 16px;
  `,
);

const MenuSectionItemTitle = styled('span', {
  target: menuSectionItemClasses.title,
})<{ ownerState: MenuSectionItemOwnerState }>(
  ({ theme }) => css`
    ${theme.typography['label/semibold/xs']};
    flex: 1 1 auto;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  `,
);

const MenuSectionItemSubheader = styled('p', {
  target: menuSectionItemClasses.subheader,
})<{ ownerState: MenuSectionItemOwnerState }>(
  ({ theme, ownerState }) => css`
    padding: 6px 16px;
    ${theme.typography['label/semibold/lg']}
    color: ${ownerState.color !== 'global'
      ? theme.color['text/weak/default']
      : theme.color['text/global/default']};
  `,
);

const MenuSectionItemContent = styled('div', {
  target: menuSectionItemClasses.content,
})<{ ownerState: MenuSectionItemOwnerState }>(
  () => css`
    /* 자식 아이템들을 감싸는 컨테이너 */
  `,
);
